import { API_URL } from '@/config';
import { formatDate } from '@/utils/dateFormat';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Users, Calendar, Cake, Heart, Phone, Mail, ArrowRight, CheckCircle, CreditCard } from 'lucide-react';
import { Button } from '@/components/ui/button';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${API_URL}/api`;

const DashboardPage = () => {
  const navigate = useNavigate();
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState('');
  const [dialogData, setDialogData] = useState([]);
  const [dialogLoading, setDialogLoading] = useState(false);

  useEffect(() => {
    loadMetrics();
  }, []);

  const loadMetrics = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/metrics`, { headers: { Authorization: `Bearer ${localStorage.getItem("session_token")}` } });
      setMetrics(response.data);
    } catch (error) {
      console.error('Error loading metrics:', error);
      toast.error('Failed to load dashboard metrics');
    } finally {
      setLoading(false);
    }
  };

  const handleCardClick = async (type) => {
    if (type === 'members') {
      navigate('/admin/members');
      return;
    }
    
    if (type === 'events') {
      navigate('/admin/events');
      return;
    }

    setDialogType(type);
    setDialogOpen(true);
    setDialogLoading(true);
    
    try {
      const response = await axios.get(`${API}/dashboard/highlights`, { headers: { Authorization: `Bearer ${localStorage.getItem("session_token")}` } });
      
      if (type === 'birthdays') {
        // Get members with birthdays this week
        const today = new Date();
        const weekLater = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);
        
        const membersRes = await axios.get(`${API}/members`, { headers: { Authorization: `Bearer ${localStorage.getItem("session_token")}` } });
        const birthdaysThisWeek = membersRes.data.filter(member => {
          if (!member.date_of_birth) return false;
          const dob = new Date(member.date_of_birth);
          const thisYearBday = new Date(today.getFullYear(), dob.getMonth(), dob.getDate());
          return thisYearBday >= today && thisYearBday <= weekLater;
        });
        setDialogData(birthdaysThisWeek);
      } else if (type === 'anniversaries') {
        // Get members with anniversaries this week
        const today = new Date();
        const weekLater = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);
        
        const membersRes = await axios.get(`${API}/members`, { headers: { Authorization: `Bearer ${localStorage.getItem("session_token")}` } });
        const anniversariesThisWeek = membersRes.data.filter(member => {
          if (!member.wedding_anniversary_date) return false;
          const anniversary = new Date(member.wedding_anniversary_date);
          const thisYearAnniversary = new Date(today.getFullYear(), anniversary.getMonth(), anniversary.getDate());
          return thisYearAnniversary >= today && thisYearAnniversary <= weekLater;
        });
        setDialogData(anniversariesThisWeek);
      }
    } catch (error) {
      toast.error('Failed to load details');
      setDialogOpen(false);
    } finally {
      setDialogLoading(false);
    }
  };

  const getDialogTitle = () => {
    if (dialogType === 'birthdays') return 'Birthdays This Week';
    if (dialogType === 'anniversaries') return 'Anniversaries This Week';
    return '';
  };

  if (loading) {
    return <div className="flex items-center justify-center py-12"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div></div>;
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="dashboard-page">
      <div>
        <h2 className="text-3xl font-bold mb-2" style={{ color: '#0F172A' }}>Dashboard Overview</h2>
        <p className="text-muted-foreground">Quick insights and metrics</p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card 
          className="hover-lift cursor-pointer transition-all" 
          onClick={() => handleCardClick('members')}
          data-testid="metric-members"
          style={{ borderTop: '3px solid #D4AF37' }}
        >
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Total Members</CardTitle>
            <Users className="w-4 h-4" style={{ color: '#D4AF37' }} />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold" style={{ color: '#0F172A' }}>{metrics?.total_members || 0}</div>
            <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
              Click to view all <ArrowRight className="w-3 h-3" />
            </p>
          </CardContent>
        </Card>
        
        <Card 
          className="hover-lift cursor-pointer transition-all" 
          onClick={() => handleCardClick('events')}
          data-testid="metric-events"
          style={{ borderTop: '3px solid #3B82F6' }}
        >
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Upcoming Events</CardTitle>
            <Calendar className="w-4 h-4" style={{ color: '#3B82F6' }} />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold" style={{ color: '#0F172A' }}>{metrics?.upcoming_events || 0}</div>
            <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
              Click to manage <ArrowRight className="w-3 h-3" />
            </p>
          </CardContent>
        </Card>
        
        <Card 
          className="hover-lift cursor-pointer transition-all" 
          onClick={() => handleCardClick('birthdays')}
          data-testid="metric-birthdays"
          style={{ borderTop: '3px solid #F59E0B' }}
        >
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Birthdays This Week</CardTitle>
            <Cake className="w-4 h-4" style={{ color: '#F59E0B' }} />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold" style={{ color: '#0F172A' }}>{metrics?.birthdays_this_week || 0}</div>
            <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
              Click to view details <ArrowRight className="w-3 h-3" />
            </p>
          </CardContent>
        </Card>
        
        <Card 
          className="hover-lift cursor-pointer transition-all" 
          onClick={() => handleCardClick('anniversaries')}
          data-testid="metric-anniversaries"
          style={{ borderTop: '3px solid #EC4899' }}
        >
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Anniversaries This Week</CardTitle>
            <Heart className="w-4 h-4" style={{ color: '#EC4899' }} />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold" style={{ color: '#0F172A' }}>{metrics?.anniversaries_this_week || 0}</div>
            <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
              Click to view details <ArrowRight className="w-3 h-3" />
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Event Confirmations Section */}
      {metrics?.event_confirmations?.length > 0 && (
        <div className="space-y-4">
          <h3 className="text-xl font-semibold" style={{ color: '#0F172A' }}>Event Confirmations</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {metrics.event_confirmations.map((event) => (
              <Card 
                key={event.event_id} 
                className="hover-lift cursor-pointer transition-all"
                onClick={() => navigate('/admin/events')}
                style={{ borderTop: '3px solid #8B5CF6' }}
              >
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium truncate pr-2">{event.title}</CardTitle>
                  <CheckCircle className="w-4 h-4 flex-shrink-0" style={{ color: '#8B5CF6' }} />
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-2xl font-bold" style={{ color: '#0F172A' }}>{event.confirmation_count}</div>
                      <p className="text-xs text-muted-foreground">Confirmations</p>
                    </div>
                    <div className="text-right">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        event.payment_type === 'paid' ? 'bg-green-100 text-green-800' :
                        event.payment_type === 'partially_paid' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-blue-100 text-blue-800'
                      }`}>
                        <CreditCard className="w-3 h-3 inline mr-1" />
                        {event.payment_type === 'paid' ? 'Paid' : 
                         event.payment_type === 'partially_paid' ? 'Partial' : 'Free'}
                      </span>
                      {event.date_time_start && (
                        <p className="text-xs text-muted-foreground mt-1">
                          {formatDate(event.date_time_start)}
                        </p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}
      
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{getDialogTitle()}</DialogTitle>
          </DialogHeader>
          
          {dialogLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : dialogData.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No {dialogType === 'birthdays' ? 'birthdays' : 'anniversaries'} this week
            </div>
          ) : (
            <div className="space-y-3">
              {dialogData.map((member) => (
                <Card key={member.user_id} className="hover-lift">
                  <CardContent className="p-4">
                    <div className="flex items-start gap-4">
                      <img
                        src={member.photo_url || 'https://api.dicebear.com/7.x/avataaars/svg?seed=Lion'}
                        alt={member.name}
                        className="w-16 h-16 rounded-full object-cover"
                      />
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold" style={{ color: '#0F172A' }}>{member.name}</h3>
                        {dialogType === 'anniversaries' && member.spouse_name && (
                          <p className="text-sm text-muted-foreground">& {member.spouse_name}</p>
                        )}
                        <div className="mt-2 space-y-1 text-sm">
                          {member.mobile && (
                            <div className="flex items-center gap-2 text-muted-foreground">
                              <Phone className="w-4 h-4" />
                              <span>{member.mobile}</span>
                            </div>
                          )}
                          <div className="flex items-center gap-2 text-muted-foreground">
                            <Mail className="w-4 h-4" />
                            <span>{member.email}</span>
                          </div>
                          <div className="flex items-center gap-2 text-muted-foreground">
                            <Calendar className="w-4 h-4" />
                            <span>
                              {dialogType === 'birthdays' 
                                ? `Birthday: ${formatDate(member.date_of_birth)}`
                                : `Anniversary: ${formatDate(member.wedding_anniversary_date)}`
                              }
                            </span>
                          </div>
                        </div>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setDialogOpen(false);
                          navigate(`/admin/members`);
                        }}
                      >
                        View Profile
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DashboardPage;
